import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  canPromptInstall,
  dismissInstallPrompt,
  isIosSafari,
  isStandaloneDisplay,
  promptInstall,
  subscribeInstallPrompt,
  wasInstallDismissed,
} from '@/lib/pwa'
import { cn } from '@/lib/utils'

type Props = {
  className?: string
  /** When true, show even if the user previously dismissed (e.g. claim-done step). */
  force?: boolean
  compact?: boolean
}

/**
 * "Add Biddle to your Home Screen" — Chromium uses beforeinstallprompt;
 * iOS Safari shows Share → Add to Home Screen instructions.
 */
export function InstallAppPrompt({ className, force = false, compact = false }: Props) {
  const [, setTick] = useState(0)

  useEffect(() => subscribeInstallPrompt(() => setTick((n) => n + 1)), [])

  if (isStandaloneDisplay()) return null
  if (!force && wasInstallDismissed()) return null

  const canPrompt = canPromptInstall()
  const ios = isIosSafari()
  if (!canPrompt && !ios) return null

  async function onInstall() {
    await promptInstall()
  }

  function onDismiss() {
    dismissInstallPrompt()
    setTick((n) => n + 1)
  }

  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-muted/40 px-4 py-3',
        className,
      )}
    >
      <p className={cn('font-medium text-foreground', compact ? 'text-sm' : 'text-base')}>
        Add Biddle to your Home Screen
      </p>
      <p className={cn('mt-1 text-muted-foreground', compact ? 'text-xs' : 'text-sm')}>
        {ios
          ? 'In Safari, tap Share, then Add to Home Screen — so you can open Biddle like an app.'
          : 'Install Biddle for quicker access next time you get buyer interest.'}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {canPrompt ? (
          <Button type="button" size="sm" variant="primary" onClick={() => void onInstall()}>
            Install
          </Button>
        ) : null}
        {!force ? (
          <Button type="button" size="sm" variant="ghost" onClick={onDismiss}>
            Not now
          </Button>
        ) : null}
      </div>
    </div>
  )
}
